import { useRef, useState, useEffect, useMemo } from "react";
import "./Register.css";
import api, { getApiErrorMessage } from "../../utils/api";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import Slider from "react-slick";
import { FaArrowLeft } from "react-icons/fa";
import { getNames } from "country-list";
import { detectUserCountry } from "../../utils/detectCountry";
import { useAuth } from "../../contexts/AuthContext";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserEnrolledCohorts } from "../../redux/slices/userEnrolledCohortSlice";

export default function RegistrationForm() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const dispatch = useDispatch();
  const { enrollment: userEnrollments } = useSelector((state) => state.userEnrolledCohorts || {});

  const isPrivateStudent = isAuthenticated && (
    user?.role === 'private_student' || 
    userEnrollments?.some(e => e.class_option === 'Private Class')
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [classOption, setClassOption] = useState("");
  const [duration, setDuration] = useState("3 Months");
  const [usdToNgn, setUsdToNgn] = useState(null);
  const [referral, setReferral] = useState("");
  const { cohort_id, course_id, cohortName, coursePrice, cohortPrice, isPrivateClass } = location.state || {};
 


  useEffect(() => {
    if (isAuthenticated && localStorage.getItem('authToken')) {
      dispatch(fetchUserEnrolledCohorts());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if ((isPrivateStudent || isPrivateClass) && coursePrice) {
      setClassOption("Private Class");
    }
  }, [isPrivateStudent, isPrivateClass, coursePrice]);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch("https://v6.exchangerate-api.com/v6/689d396c0a109d335cc4092c/latest/USD");
        const data = await res.json();
        setUsdToNgn(data.conversion_rates?.NGN);
      } catch (e) {
        setUsdToNgn(null);
      }
    };
    fetchRate();
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.name) setName(user.name);
      if (user.email) setEmail(user.email);
      if (user.phone) setPhone(user.phone);
      if (user.country) setCountry(user.country);
    }
  }, [isAuthenticated, user]);
  const countries = useMemo(
    () => getNames().sort((a, b) => a.localeCompare(b)),
    []
  );
  const [status, setStatus] = useState({
    status: false,
    class: "button",
    message: "Loading...",
  });

  const [errorMessage, setErrorMessage] = useState("");

  // Submission state for button UI
  const [submitted, setSubmitted] = useState(false);

  // Form ref for validation
  const formRef = useRef(null);

  // Timeout ref to clear on unmount
  const timeoutRef = useRef(null);

  // Auto-detect visitor's country and pre-fill the dropdown
  useEffect(() => {
    const autoDetectCountry = async () => {
      const { country: detectedCountry } = await detectUserCountry();
      if (detectedCountry) {
        setCountry(detectedCountry);
      }
    };
    autoDetectCountry();
  }, []);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = formRef.current;
    if (!formEl) return;

    // Check native form validation
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    // Prevent multiple submissions
    if (submitted) return;

    const formData = { 
      name, 
      email, 
      phone, 
      country, 
      cohort_id: cohort_id || null, 
      course_id: course_id || null, 
      class_option: classOption, 
      referral_code: referral,
      duration: classOption === "Private Class" ? duration : null
    };

    try {
      setSubmitted(true);
      setErrorMessage("");

      if (referral) {
        try {
          await axios.post(
            process.env.REACT_APP_REFERRAL_API_URL,
            {
              code: referral,
              referred_user_name: name,
              app_identifier: 'Training Hub',
              metadata: { email, phone, class_option: classOption, country, duration: classOption === "Private Class" ? duration : null }
            },
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${process.env.REACT_APP_REFERRAL_API_KEY}`
              }
            }
          );
        } catch (error) {
          setSubmitted(false);
          setStatus({
            status: false,
            class: "button",
            message: "Loading...",
          });
          const msg = getApiErrorMessage(error, "Invalid referral code.");
          setErrorMessage(`Referral Code Error: ${msg}`);
          return;
        }
      }

      await api.post(`/cohorts/register`, formData);

      // Initialize payment and redirect

      const redirect = await api.post(`/payment/initialize`, {
        email,
        name,
        class_option: classOption,
        phone,
        country,
        cohort_id: cohort_id || null,
        course_id: course_id || null,
        referral_code: referral,
        duration: classOption === "Private Class" ? duration : null,
      });

      setStatus({
        class: "button-success",
        message: "Redirecting....",
        status: true,
      });

      if (redirect.data?.status === "success" && redirect.data?.data?.authorization_url) {
        const url = redirect.data.data.authorization_url;
        if (url) {
          window.location.href = url;
          return;
        }
      } else {
        throw new Error(redirect.data?.message || "Failed to initialize payment.");
      }
    } catch (error) {
      setStatus({
        class: "button-error",
        message: "An error occurred",
        status: false,
      });
      setErrorMessage(
        getApiErrorMessage(error, "Registration failed. Please try again.")
      );
      setSubmitted(false);
      return; // Prevent falling through to the timeout reset on error
    }

    // Reset after 3 seconds only if still mounted (success fallback)
    timeoutRef.current = setTimeout(() => {
      if (!isAuthenticated) {
        setName("");
        setEmail("");
        setPhone("");
        setCountry("");
      }
      setReferral("");
      setStatus({
        status: false,
        class: "button",
        message: "Loading...",
      });
      setSubmitted(false);
      setErrorMessage("");
    }, 3000);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    arrows: false,
    pauseOnHover: true,
    appendDots: (dots) => (
      <div className="slider-dots">
        <ul style={{ margin: "0px" }}> {dots} </ul>
      </div>
    ),
    customPaging: () => <div className="dot"></div>,
  };

  const slides = [
    {
      title: "Join the Next GritinAI Cohort",
      description:
        "Be part of an intensive, hands-on learning experience designed to accelerate your tech career and put you ahead of the curve.",
    },
    {
      title: "Learn With a Community",
      description:
        "Collaborate with driven learners, attend live sessions, and get mentorship from industry experts throughout the cohort.",
    },
    {
      title: "Start Your Transformation",
      description:
        "Register now and unlock structured learning, real-world projects, accountability, and support from a community that wants you to win.",
    },
  ];

  return (
    <>
      <div className="register-container">
        <div className="register-wrapper">
          <div className="user-register-left-panel">
            <div className="form-scroll-container" aria-live="polite">
              <Link to="/cohorts">
                <button className="back-button">
                  <FaArrowLeft size={25} />
                </button>
              </Link>
              <div className="user-register-form-header">
                <h1>Start Your Adventure 🚀</h1>
                <p>
                  Register for the cohort{" "}
                  <span>
                    <b>{cohortName}</b>
                  </span>
                </p>
              </div>

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="user-register-form"
              >
                <div className="input-group">
                  <label className="label" htmlFor="name">
                    Full Name
                  </label>
                  <input
                    id="name"
                    className="input"
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    disabled={isAuthenticated}
                  />
                </div>

                <div className="input-group">
                  <label className="label" htmlFor="email">
                    Email Address
                  </label>
                  <input
                    id="email"
                    className="input"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    disabled={isAuthenticated}
                  />
                </div>

                <div className="input-group">
                  <label className="label" htmlFor="phone">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    className="input"
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+234 80 2293 1920"
                    required
                  />
                </div>

                <div className="input-group">
                  <label className="label" htmlFor="country">
                    Country
                  </label>
                  <select
                    id="country"
                    className="input"
                    name="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                  >
                    <option value="">Select your country</option>
                    {countries.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="input-group">
                  <label htmlFor="class_option">Class Option</label>
                  <select
                    id="class_option"
                    className="input"
                    name="class_option"
                    value={classOption}
                    onChange={(e) => setClassOption(e.target.value)}
                    required
                  >
                    <option value="">Select a class option</option>
                    {!(isPrivateStudent || isPrivateClass) ? (
                      <>
                        <option value="weekday">Weekday</option>
                        <option value="weekend">Weekend</option>
                      </>
                    ) : (
                      coursePrice && (
                        <option value="Private Class">Private Class</option>
                      )
                    )}
                  </select>
                </div>

                {classOption === "Private Class" && (
                  <div className="input-group">
                    <label className="label" htmlFor="duration">
                      Preferred Duration
                    </label>
                    <select
                      id="duration"
                      className="input"
                      name="duration"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      required
                    >
                      <option value="3 Months">3 Months</option>
                      <option value="6 Months">6 Months</option>
                      <option value="9 Months">9 Months</option>
                      <option value="12 Months">12 Months</option>
                    </select>
                  </div>
                )}

                {classOption === "Private Class" && coursePrice && (
                  <div className="price-info-box mb-3" style={{ padding: '12px', backgroundColor: '#eef2f7', borderLeft: '4px solid #0056b3', borderRadius: '4px', fontSize: '0.9rem', color: '#333' }}>
                    <strong>Course Price:</strong> ${parseFloat(coursePrice).toLocaleString()} 
                    {usdToNgn && ` (₦${(parseFloat(coursePrice) * usdToNgn).toLocaleString()})`}
                  </div>
                )}

                {classOption && classOption !== "Private Class" && cohortPrice && (
                  <div className="price-info-box mb-3" style={{ padding: '12px', backgroundColor: '#eef2f7', borderLeft: '4px solid #10b981', borderRadius: '4px', fontSize: '0.9rem', color: '#333' }}>
                    <strong>Cohort Price:</strong> ${parseFloat(cohortPrice).toLocaleString()} 
                    {usdToNgn && ` (₦${(parseFloat(cohortPrice) * usdToNgn).toLocaleString()})`}
                  </div>
                )}

                <div className="input-group">
                  <label className="label" htmlFor="referral">
                    Referral (Optional)
                  </label>
                  <input
                    id="referral"
                    className="input"
                    type="text"
                    name="referral"
                    value={referral}
                    onChange={(e) => setReferral(e.target.value)}
                    placeholder="Who referred you? (e.g., Code, Name, or Social Media)"
                  />
                </div>

                {errorMessage && (
                  <div className="error-message" role="alert">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className={submitted ? status.class : "btn-submit"}
                  disabled={submitted}
                  aria-disabled={submitted}
                >
                  {submitted ? (
                    <>&nbsp;{status.message}</>
                  ) : (
                    "Proceed to payment"
                  )}
                </button>
              </form>
            </div>
          </div>

          <div className="user-register-right-panel">
            <div className="slider-overlay"></div>
            <div className="slider-content">
              <div className="brand-section">
                <div className="brand-logo">
                  <span className="logo-icon">
                    <img src="/login-logo.png" alt="" />
                  </span>
                  <span className="brand-name">GritinAI</span>
                </div>
              </div>

              <div className="slider-main">
                <Slider {...sliderSettings}>
                  {slides.map((slide, index) => (
                    <div key={index} className="slide-item">
                      <div className="slide-content">
                        <div className="slide-text">
                          <h2>{slide.title}</h2>
                          <p>{slide.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
